// Import components
const { SingleRug } = require('./SingleRug.js');
const { RugCanvas } = require('./RugCanvas.js');

// Import constants
const { formatCurrency } = require('../../constants.js');

const RugCalculator = {
    name: 'RugCalculator',
    components: {
        SingleRug,
        RugCanvas
    },
    props: {
        postItemToCart: {
            type: Function,
            required: true
        },
        addToCartLoading: {
            type: Boolean,
            required: true
        }
    },
    setup(props) {
        const { ref, computed, onMounted, nextTick } = Vue;
        const { useStore } = Vuex;

        const store = useStore();
        const isOpen = ref(false); // Start with dropdown open
        const plannerOpen = ref(false);

        const toggleDropdown = () => {
            isOpen.value = !isOpen.value;
        };

        const selectSize = (size) => {
            // If it's the same size, just toggle the dropdown
            if (store.state.selectedSize && store.state.selectedSize.rug_ean_code === size.rug_ean_code) {
                toggleDropdown();
                return;
            }

            // Set the size
            store.commit('updateRugSize', size);

            // If open, close it immediately
            if (isOpen.value) {
                isOpen.value = false;
            }
        };

        // Computed property to determine which rugs to display
        const visibleRugs = computed(() => {
            if (isOpen.value) {
                // When open, show all rugs
                return store.state.rugSizes;
            } else if (store.state.selectedSize) {
                // When closed, show only the selected rug
                return store.state.rugSizes.filter(size =>
                    size.rug_ean_code === store.state.selectedSize.rug_ean_code
                );
            }
            // Fallback to all rugs if nothing is selected
            return store.state.rugSizes;
        });

        const lowestPrice = computed(() => {
            return store.state.rugSizes.reduce((min, size) => {
                // Use sale price if available, otherwise use default price
                const effectivePrice = size.rug_sale_price || size.rug_default_price;
                return Math.min(min, effectivePrice);
            }, store.state.rugSizes[0].rug_default_price);
        });

        const selectedSizeText = computed(() => {
            if (!store.state.selectedSize) return '';
            return `${store.state.selectedSize.rug_width}cm x ${store.state.selectedSize.rug_length}cm ${store.state.selectedSize.rug_variable_title}`;
        });

        return {
            store,
            isOpen,
            plannerOpen,
            visibleRugs,
            toggleDropdown,
            selectSize,
            lowestPrice,
            selectedSizeText,
            formatCurrency
        };
    },
    template: `
        <div class="flex items-center justify-between w-full mt-5">
            <div class="text-sm">Measurements in</div>
            <div class="text-sm"></div>
        </div>

        <div class="relative w-full mb-5">
            <div @click="toggleDropdown">
                <div class="dropdown-default" :class="{ 'open': isOpen, 'selected': store.state.selectedSize && !isOpen }">
                    <div class="text-sm font-medium text-shades-800">
                        Select a size ({{ store.state.rugSizes.length }} sizes available)
                    </div>
                    <div class="flex items-center justify-end space-x-2">
                        <div class="font-bold text-shades-800">from {{ formatCurrency(lowestPrice) }}</div>
                        <span class="transition-transform duration-300 fill-shades-800" :class="{ 'rotate-180': isOpen }">
                            <svg width="10" height="10" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                                <path d="M256 82.7l22.6 22.6 192 192L493.3 320 448 365.3l-22.6-22.6L256 173.3 86.6 342.6 64 365.3 18.7 320l22.6-22.6 192-192L256 82.7z" />
                            </svg>
                        </span>
                    </div>
                </div>
            </div>
            
            <div v-show="isOpen || store.state.selectedSize" class="dropdown-options-container rounded-b-md w-full bg-shades-25 overflow-hidden">
                <SingleRug
                    v-for="(size, index) in visibleRugs"
                    :key="index"
                    :size="size"
                    @click.stop="selectSize(size)"
                />
            </div>
        </div>

        <div class="w-full mt-5 mb-5" v-if="store.state.selectedSize">
            <div class="text-sm font-medium text-shades-800">
                Need help finding the right size? Try our rug to room size guide.
            </div>
            <div class="btn btn-lightGrey w-full md:w-auto" @click="plannerOpen = !plannerOpen">
                <span v-if="!plannerOpen">Rug to room size guide</span>
                <span v-if="plannerOpen">Close</span>
            </div>
        </div>

        <div class="mt-5 mb-5" v-if="plannerOpen">
            <RugCanvas :size="store.state.selectedSize" />
        </div>
    `
};

// Export the component using module.exports
module.exports = { RugCalculator };
